/**
 * Detect and handle expired/missing browser sessions: clear client auth and
 * send the user to the sign-in page instead of surfacing an API error.
 */

import { clearConnectorSnapshot } from "../utils/connectorSnapshotCache";

let redirectInProgress = false;
let sessionExpiredHandler = null;

/**
 * True when an API error message indicates the browser session is missing or expired.
 *
 * @example
 * isSessionAuthErrorMessage("Invalid or expired session");
 * // true
 *
 * @example
 * isSessionAuthErrorMessage("Gmail is not connected");
 * // false
 */
export const isSessionAuthErrorMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized.includes("invalid or expired session") ||
    normalized.includes("missing session") ||
    normalized.includes("session id is missing")
  );
};

/**
 * Register the app handler that clears React auth state on session expiry.
 * AuthProvider should call this once on mount.
 *
 * @example
 * registerSessionExpiredHandler(() => {
 *   setUser(null);
 *   setIsAuthenticated(false);
 * });
 */
export const registerSessionExpiredHandler = (handler) => {
  sessionExpiredHandler =
    typeof handler === "function" ? handler : null;
};

/**
 * Allow a future session-expiry redirect after the user signs in again.
 *
 * @example
 * resetSessionExpiredRedirectGate();
 * // Output: next expired-session API error can redirect again
 */
export const resetSessionExpiredRedirectGate = () => {
  redirectInProgress = false;
};

/**
 * True when the current path is already the public sign-in surface.
 *
 * @example
 * // Input: pathname "/"
 * isAlreadyOnLoginPage();
 * // true
 */
export const isAlreadyOnLoginPage = () => {
  if (typeof window === "undefined") {
    return false;
  }
  const path = window.location.pathname;
  return path === "/" || path === "/signin" || path === "/signup";
};

/**
 * Persist the current path, clear client session markers, and hard-refresh to login.
 * Safe to call multiple times; only the first call runs until the gate is reset.
 * Always uses a full navigation so OAuth/session errors never linger as in-page UI.
 *
 * @example
 * // Input: window at "/business/action-items", expired session API error
 * redirectToLoginOnSessionExpired();
 * // Output: sessionStorage.redirectPath set, auth cleared, location → "/"
 */
export const redirectToLoginOnSessionExpired = () => {
  if (redirectInProgress || typeof window === "undefined") {
    return;
  }
  redirectInProgress = true;

  try {
    const returnPath = `${window.location.pathname}${window.location.search || ""}`;
    if (
      returnPath &&
      !isAlreadyOnLoginPage() &&
      !returnPath.startsWith("/signin") &&
      !returnPath.startsWith("/signup")
    ) {
      // Keep path only (drop OAuth query junk like ?code=).
      const pathOnly = window.location.pathname;
      if (pathOnly && pathOnly !== "/") {
        sessionStorage.setItem("redirectPath", pathOnly);
      }
    }
    localStorage.removeItem("user_info");
    localStorage.removeItem("access_token");
    // Cached connector rows are per-user; don't leave them behind on expiry.
    clearConnectorSnapshot();
  } catch (_) {
    // Best-effort client cleanup.
  }

  if (sessionExpiredHandler) {
    try {
      sessionExpiredHandler();
    } catch (error) {
      console.error("Session expired handler failed:", error);
    }
  }

  // Hard refresh to a clean login URL — never leave stale error/query state on screen.
  window.location.replace("/");
};

/**
 * True when a Google login callback failed in a way that should bounce to a clean login page.
 *
 * @example
 * isGoogleLoginAuthFailureMessage('{"error":"invalid_grant"}');
 * // true
 *
 * @example
 * isGoogleLoginAuthFailureMessage("Gmail is not connected");
 * // false
 */
export const isGoogleLoginAuthFailureMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized.includes("invalid_grant") ||
    normalized.includes("id token not found") ||
    normalized.includes("authorization code not found") ||
    normalized.includes("login oauth callback failed")
  );
};

/**
 * If `message` is a session-auth error, redirect to login and return true.
 *
 * @example
 * // Input: "Invalid or expired session"
 * // Output: true (redirect started)
 *
 * @example
 * // Input: "Failed to fetch workflows"
 * // Output: false
 */
export const maybeRedirectOnSessionAuthError = (message) => {
  if (!isSessionAuthErrorMessage(message)) {
    return false;
  }
  redirectToLoginOnSessionExpired();
  return true;
};

/**
 * If `message` is a recoverable Google login failure, hard-refresh to login.
 *
 * @example
 * // Input: '{"error":"invalid_grant"}'
 * // Output: true (redirect started)
 */
export const maybeRedirectOnGoogleLoginAuthFailure = (message) => {
  if (!isGoogleLoginAuthFailureMessage(message)) {
    return false;
  }
  redirectToLoginOnSessionExpired();
  return true;
};

const shouldIgnoreSessionExpiryUrl = (url) => {
  const value = String(url || "");
  return (
    value.includes("/validate-session") ||
    value.includes("/auth/google/callback") ||
    value.includes("/logout")
  );
};

const resolveFetchUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input && typeof input.url === "string") {
    return input.url;
  }
  return "";
};

/**
 * Wrap `window.fetch` so 401/403 session errors redirect to login once.
 * Idempotent; safe to call from AuthProvider mount.
 *
 * @example
 * installSessionExpiryFetchGuard();
 * // Subsequent fetch() calls that return "Invalid or expired session" redirect to "/"
 */
export const installSessionExpiryFetchGuard = () => {
  if (typeof window === "undefined" || window.__starmeshSessionFetchGuard) {
    return;
  }
  window.__starmeshSessionFetchGuard = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    if (response.status !== 401 && response.status !== 403) {
      return response;
    }
    if (shouldIgnoreSessionExpiryUrl(resolveFetchUrl(input))) {
      return response;
    }
    try {
      const payload = await response.clone().json();
      if (isSessionAuthErrorMessage(payload?.error)) {
        redirectToLoginOnSessionExpired();
      }
    } catch (_) {
      // Non-JSON error bodies are ignored.
    }
    return response;
  };
};
